import type { FC, FormEvent, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'

import Link from 'next/link'
import { useState } from 'react'
import { useRouter } from 'next/router'
import { ArrowRight, LoaderCircle } from 'lucide-react'
import { sendJson } from '@/lib/request'
import { getResident, safeNextPath } from '@/lib/auth'
import { Seo } from '@/components/generals/seo'
import { Button } from '@/components/generals/button'
import { AuthShell } from '@/components/portal/auth-shell'
import { PasswordInput } from '@/components/portal/password-input'
import { Field, FormMessage, TextInput } from '@/components/generals/form-field'

interface SignInProps {
    next: string
}

/*
   Resident sign-in
   Email and password on the access layout. Signed-in residents skip straight
   to the portal.
*/
const SignIn: FC<SignInProps> = ({ next }): ReactNode => {
    const router = useRouter()
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState('')

    const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        if (!email.trim() || !password) {
            setError("Please enter your email address and password.")
            return
        }

        setBusy(true)
        setError('')

        const result = await sendJson<{ redirect?: string }>('/api/residents/login', 'POST', { email, password, next })

        if (result.ok) {
            await router.replace(result.data.redirect ?? '/residents/dashboard')
            return
        }

        setBusy(false)
        setPassword('')
        setError(result.data.message ?? "We could not sign you in. Please try again.")
    }

    return (
        <AuthShell
            eyebrow='Secure resident access'
            title='Sign in to the resident portal'
            intro='Notices, estate documents and gate bookings for Rockwood residents.'
        >
            <Seo title='Resident Access' description='Secure sign-in for residents of Rockwood Estate.' noIndex />

            <form noValidate onSubmit={onSubmit} className='space-y-5'>
                <Field id='email' label='Email address'>
                    <TextInput
                        id='email'
                        name='email'
                        type='email'
                        autoComplete='email'
                        value={email}
                        onChange={event => setEmail(event.target.value)}
                        required
                    />
                </Field>

                <Field id='password' label='Password'>
                    <PasswordInput
                        id='password'
                        name='password'
                        autoComplete='current-password'
                        value={password}
                        onChange={event => setPassword(event.target.value)}
                        required
                    />
                </Field>

                {error && (
                    <FormMessage tone='error'>
                        {error}
                    </FormMessage>
                )}

                <Button type='submit' size='lg' disabled={busy} className='w-full'>
                    {busy
                        ? <LoaderCircle aria-hidden='true' className='size-4 animate-spin' />
                        : null}

                    {busy ? 'Signing in' : 'Sign in'}

                    {!busy && <ArrowRight aria-hidden='true' className='size-4 transition-transform duration-300 group-hover/button:translate-x-0.5' />}
                </Button>
            </form>

            <div className='mt-8 rounded-2xl border border-stone bg-sand p-5 text-sm leading-relaxed'>
                <p className='font-medium'>
                    First time here, or forgotten your password?
                </p>

                <p className='mt-1 text-muted-foreground'>
                    We'll email you a one-time code to set a new password.
                </p>

                <Link href='/residents/access' className='link-underline mt-3 inline-block pb-0.5 font-medium text-forest'>
                    Set up or reset access
                </Link>
            </div>
        </AuthShell>
    )
}

export const getServerSideProps: GetServerSideProps<SignInProps> = async context => {
    context.res.setHeader('Cache-Control', 'private, no-store')

    const next = safeNextPath(context.query.next)
    const resident = await getResident(context.req).catch(() => null)

    if (resident) return { redirect: { destination: next, permanent: false } }

    return { props: { next } }
}

export default SignIn
